import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import useLocations from '../../hooks/useLocations';
import useTimeslotPicker from '../../hooks/useTimeslotPicker';
import useReduxKey from '../../hooks/useReduxKey';

import CompanyHeader from '../../components/CompanyHeader/CompanyHeader';
import LocationPicker from '../../components/LocationPicker';
import GroupSize from '../../components/GroupSizePicker';
import DatePicker from '../../components/DatePicker';
import TimeSlotPicker from '../../components/TimeSlotPicker';
import StickyFooter from '../../components/StickyFooter';
import ReservationTypePicker from '../../components/ReservationTypePicker';
import filterActions from '../../actions/filterActions';
import { FilterState } from '../../reducers/filter';
import { Configuration } from '../../reducers/configuration';
import './styles.scss';

export default () : JSX.Element => {
  const {
    selectedLocation,
    groupSize: selectedGroupSize,
    reservationType: selectedReservationType,
    startDate: selectedDate,
    selectedTimeSlot,
  } = useReduxKey<FilterState>('filterState');
  const { t } = useTranslation();
  const { setGroupSize, setReservationType, resetSelectedTimeSlot } = filterActions();
  const [loading, setLoading] = useState(true);
  const resources = useSelector((state: any) => state.resources);

  useEffect(() => {
    if (resources.resourceTree) {
        setLoading(false);
    }
  }, [resources])
  let hasResources = false;

  let showSelections = {
    location: false,
    groupSize: false,
    date: false,
    timeSlot: false,
    reservationType: false,
  };

  let disabledSelections = {
    groupSize: false,
    date: false,
    timeSlot: false,
    reservationType: false,
  };

  useEffect(() => {
    resetSelectedTimeSlot();
  }, [selectedDate, selectedReservationType]);

  if (resources?.resourceTree?.length > 0) {
    hasResources = true;
  }

  if (
    selectedLocation != null
    && selectedGroupSize != null
    && selectedReservationType != null
    && selectedDate != null
    && selectedTimeSlot != null
  ) {
    showSelections = { ...showSelections, timeSlot: true };
  } else if (selectedLocation == null) {
    showSelections = {
      ...showSelections,
      location: true,
    };

    disabledSelections = {
      groupSize: true,
      reservationType: true,
      date: true,
      timeSlot: true,
    };
  } else if (selectedGroupSize == null) {
    showSelections = {
      ...showSelections,
      groupSize: true,
    };

    disabledSelections = {
      ...disabledSelections,
      reservationType: true,
      date: true,
      timeSlot: true,
    };
  } else if (selectedReservationType == null) {
    showSelections = {
      ...showSelections,
      reservationType: true,
    };

    disabledSelections = {
      ...disabledSelections,
      date: true,
      timeSlot: true,
    };
  } else if (selectedDate == null) {
    showSelections = {
      ...showSelections,
      date: true,
    };

    disabledSelections = {
      ...disabledSelections,
      timeSlot: true,
    };
  } else if (selectedTimeSlot == null) {
    showSelections = {
      ...showSelections,
      timeSlot: true,
    };
  }

  const { searchParams } = useMemo(() => new URL(window.location.href), []);
  const defaultLocation = useMemo(() => searchParams.get('resource') || undefined, [searchParams]);

  const locations = useLocations(defaultLocation);
  const timeslots = useTimeslotPicker(
    selectedLocation,
    selectedGroupSize,
    selectedDate,
    selectedReservationType,
  );

  const { showGroupSizeStep, groupSizeMax, userReservationTypes } = useReduxKey<Configuration>('configuration');

  useEffect(() => {
    if (!showGroupSizeStep) {
      setGroupSize(1);
    }
  }, [showGroupSizeStep]);

  useEffect(() => {
    if (userReservationTypes.length === 0) {
      setReservationType('-');
    }
  }, [userReservationTypes]);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
      </div>
    );
  }

  if (!hasResources) {
    return (
      <>
        <CompanyHeader />

        <div className="picker-wrapper">
          <p className="no-resources-configured-text">{t('no_configured_resources', 'No configured resources')}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <CompanyHeader />

      <div className="picker-wrapper">
        <LocationPicker
          locations={locations}
          showDetailsOnStart={showSelections.location}
        />
        {showGroupSizeStep && (
          <GroupSize
            disabled={disabledSelections.groupSize}
            showDetailsOnStart={showSelections.groupSize}
            maxGroupSize={groupSizeMax}
          />
        )}
        {userReservationTypes.length > 0 && (
          <ReservationTypePicker
            reservationTypes={userReservationTypes}
            disabled={disabledSelections.reservationType}
            showDetailsOnStart={showSelections.reservationType}
          />
        )}
        <DatePicker
          disabled={disabledSelections.date}
          showDetailsOnStart={showSelections.date}
        />
        <TimeSlotPicker
          timeslots={timeslots}
          disabled={disabledSelections.timeSlot}
          showDetailsOnStart={showSelections.timeSlot}
        />
      </div>

      <StickyFooter showDetailsOnStart={showSelections.location} />
    </>
  );
};
